// store/service.ts
import {
  AuthApi,
  Configuration,
  UserApi,
  OrganizationApi,
  EntryApi,
  FileApi,
  FolderApi,
  ProjectApi
} from '../service';
import { useAtom } from 'jotai';
import { userAtom } from './atoms';
import { RevisdomAPIViewModelsUserCreateUserRequest, RevisdomAPIViewModelsEntryCreateCommentRequest } from '../service/models';
import {
  ApiFolderPostRequest,
  ApiFolderEditPostRequest,
} from '../service/apis/FolderApi';
import {
  ApiFilePostRequest,
  ApiFileEditPostRequest,
} from '../service/apis/FileApi';
import {
  ApiProjectAllGetScopeEnum,
  ApiProjectEditPostRequest,
} from '../service/apis/ProjectApi';
import { useNavigate } from 'react-router-dom';

// Aynı path kısa sürede birden fazla çağrılırsa (AuthChecker + ProjectDetail + FileDetail) tek istek at
const entryByPathCache = new Map<string, { result: any; at: number }>();
const ENTRY_PATH_CACHE_MS = 800;

const baseAuthApi = new AuthApi(new Configuration());
const baseUserApi = new UserApi(new Configuration());
const baseOrgApi = new OrganizationApi(new Configuration());
const baseProjectApi = new ProjectApi(new Configuration());
const baseEntryApi = new EntryApi(new Configuration());
const baseFileApi = new FileApi(new Configuration());
const baseFolderApi = new FolderApi(new Configuration());


const postConfig = (additionalHeaders?: Record<string, string>) => {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  return {
    headers,
    credentials: 'include' as RequestCredentials,
    mode: 'cors' as RequestMode
  };
};



export const useSecureService = () => {
  const [, setUser] = useAtom(userAtom);
  const navigate = useNavigate();

  const normalizeEntryPath = (rawPath: string) => {
    const cleaned = (rawPath || '').trim().replace(/^\/+/, '');
    // URL bazen zaten encoded (%2F) geliyor; önce decode edip canonical hale getiriyoruz.
    try {
      return decodeURIComponent(cleaned);
    } catch {
      return cleaned;
    }
  };

  const handleAuthError = (error: any) => {
    // Sadece 401 Unauthorized = oturum geçersiz, logout yap. 403 Forbidden = yetki yok, logout yapma.
    if (error?.status === 401) {
      setUser(undefined);
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
      const isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login', 'results'].includes(pathSegments[0]);
      if (!isFileDetailPage) {
        window.alert('Your session has expired. You are being redirected to the login page.');
        navigate('/login');
      }
    }
  };

  function checkAndHandleAuthError(response: any) {
    if (response && response.success === false && response.status === 401) {
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
      const isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login', 'results'].includes(pathSegments[0]);
      if (!isFileDetailPage) {
        handleAuthError({ status: response.status });
      }
    }
  }

  const doLogin = async (email: string, password: string) => {
    try {
      const result = await baseAuthApi.apiAuthSigninPost({
        revisdomAPIViewModelsSignInRequest: {
          email,
          password
        }
      }, postConfig());

      checkAndHandleAuthError(result);

      if (result.success) {
        const meResult = await baseUserApi.apiUserMeGet(postConfig());
        if (meResult.success) {
          setUser(meResult.data);
        }
        return result;
      }

      return result;
    } catch (err: any) {
      handleAuthError(err);
      return {
        success: false,
        message: 'An error occurred during login'
      };
    }
  };

  const checkAuth = async () => {
    try {
      const result = await baseUserApi.apiUserMeGet(postConfig());
      if (result.success && result.data) {
        setUser(result.data);
        return true;
      }
      setUser(undefined);
      
      // File detail sayfasında ise login yönlendirmesi yapma
      const currentPath = window.location.pathname;
      const fullPath = currentPath.substring(1); // Başındaki /'yi kaldır
      
      let isFileDetailPage = false;
      
      if (fullPath) {
        try {
          // getEntryByPath ile entry tipini kontrol et
          const entryResponse = await getEntryByPath(fullPath);
          if (entryResponse.success && entryResponse.data) {
            isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
            
          }
        } catch (entryError) {
          
          // getEntryByPath başarısız olursa varsayılan kontrolü kullan
          const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
          isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
        }
      }
      
      
      
      if (!isFileDetailPage) {
        
        navigate('/login');
      } else {
        
      }
      
      return false;
    } catch (error: any) {
      setUser(undefined);
      
      // File detail sayfasında ise login yönlendirmesi yapma
      const currentPath = window.location.pathname;
      const fullPath = currentPath.substring(1); // Başındaki /'yi kaldır
      
      let isFileDetailPage = false;
      
      if (fullPath) {
        try {
          // getEntryByPath ile entry tipini kontrol et
          const entryResponse = await getEntryByPath(fullPath);
          if (entryResponse.success && entryResponse.data) {
            isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
            
          }
        } catch (entryError) {
          
          // getEntryByPath başarısız olursa varsayılan kontrolü kullan
          const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
          isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
        }
      }
      
      
      
      if (!isFileDetailPage) {
        
        navigate('/login');
      } else {
        
      }
      
      return false;
    }
  };

  // Düzenli oturum kontrolü için fonksiyon
  const startSessionCheck = () => {
    let silentFailureCount = 0;

    const shouldRedirectFor = (obj: any) => {
      try {
        const msg = (obj?.message || '').toString().toLowerCase();
        const status = obj?.status || obj?.statusCode || obj?.response?.status;
        return status === 401 || status === 419 || msg.includes('unauthorized') || msg.includes('session expired');
      } catch {
        return false;
      }
    };

    const checkOnce = async () => {
      try {
        const result = await baseUserApi.apiUserMeGet(postConfig());
        if (result?.success && result.data) {
          setUser(result.data);
          silentFailureCount = 0;
          return;
        }

        if (shouldRedirectFor(result)) {
          setUser(undefined);

          // File detail sayfasında isek yönlendirme yapma
          const currentPath = window.location.pathname;
          const fullPath = currentPath.substring(1);
          let isFileDetailPage = false;

          if (fullPath) {
            try {
              const entryResponse = await getEntryByPath(fullPath);
              if (entryResponse.success && entryResponse.data) {
                isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
              }
            } catch (entryError) {
              const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
              isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
            }
          }

          if (!isFileDetailPage) {
            window.alert('Your session has expired. You are being redirected to the login page.');
            navigate('/login');
          }
        } else {
          // Ağ hataları vb. durumlarda sessiz kal, kullanıcıyı düşürme
          silentFailureCount++;
        }
      } catch (error: any) {
        if (shouldRedirectFor(error)) {
          setUser(undefined);

          const currentPath = window.location.pathname;
          const fullPath = currentPath.substring(1);
          let isFileDetailPage = false;

          if (fullPath) {
            try {
              const entryResponse = await getEntryByPath(fullPath);
              if (entryResponse.success && entryResponse.data) {
                isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
              }
            } catch (entryError) {
              const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
              isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
            }
          }

          if (!isFileDetailPage) {
            window.alert('Your session has expired. You are being redirected to the login page.');
            navigate('/login');
          }
        } else {
          // Ağ hatası vb. durumlar – sessiz kal
          silentFailureCount++;
        }
      }
    };

    // 10 dakikada bir kontrol et
    const checkInterval = setInterval(checkOnce, 10 * 60 * 1000); // 10 dakika

    // Cleanup fonksiyonu döndür
    return () => {
      clearInterval(checkInterval);
    };
  };

  const doLogout = async () => {
    try {
      await baseAuthApi.apiAuthLogoutPost(postConfig());
      setUser(undefined);
      return true;
    } catch (error: any) {
      handleAuthError(error);
      return false;
    }
  };

  const getOrganizations = async () => {
    try {
      const response = await baseOrgApi.apiOrganizationListGet(postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading organizations'
      };
    }
  };

  const createUser = async (userData: RevisdomAPIViewModelsUserCreateUserRequest) => {
    try {
      const response = await baseUserApi.apiUserPost({
        revisdomAPIViewModelsUserCreateUserRequest: userData
      }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the user'
      };
    }
  };

  const createProject = async (data: {
    title: string;
    path: string;
    scope?: ApiProjectAllGetScopeEnum;
    location?: string;
    share?: string[];
    status?: number;
    thumbnail?: File | null;
  }) => {
    try {
      const formData = new FormData();
      formData.append('Name', data.title); // API Name field'ı bekliyor
      formData.append('Title', data.title);
      formData.append('Path', data.path);
      if (data.scope) formData.append('Scope', data.scope);
      if (data.location) formData.append('Location', data.location);
      if (data.status !== undefined) formData.append('Status', String(data.status));
      if (data.share && data.share.length > 0) {
        data.share.forEach(email => formData.append('Share', email));
      }
      if (data.thumbnail) formData.append('Thumbnail', data.thumbnail);

      const response = await fetch('https://api.revisdom.com/api/Project', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('createProject error:', response.status, errorText);
        if (response.status === 401) {
          navigate('/login');
          return { success: false, message: 'Session expired', response: errorText };
        }
        return { success: false, message: `HTTP ${response.status}: ${errorText}`, response: errorText };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the project'
      };
    }
  };

  const getProjects = async (scope?: ApiProjectAllGetScopeEnum) => {
    try {
      const response = await baseProjectApi.apiProjectAllGet({ scope }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      console.error('getProjects error:', error);
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading projects'
      };
    }
  };

  const getEnrtyDetail = async (params: { projectId?: number; folderId?: number }) => {
    try {
      const response = await baseEntryApi.apiEntryGet(params, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading entry detail'
      };
    }
  };

  const getEntryByPath = async (path: string) => {
    const normalizedPath = normalizeEntryPath(path);
    const now = Date.now();
    const cached = entryByPathCache.get(normalizedPath);
    if (cached && now - cached.at < ENTRY_PATH_CACHE_MS) {
      return typeof cached.result === 'object' && cached.result !== null
        ? { ...cached.result }
        : cached.result;
    }
    try {
      const url = `https://api.revisdom.com/api/Entry/${encodeURIComponent(normalizedPath)}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          handleAuthError({ status: response.status });
        }
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }

      const result = await response.json();
      checkAndHandleAuthError(result);
      entryByPathCache.set(normalizedPath, { result, at: Date.now() });
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading entry detail'
      };
    }
  };

  const getBreadcrumb = async (url: string) => {
    try {
      // Breadcrumb endpoint'inde de slash encode problemini önlemek için manuel çağrı
      const normalizedPath = normalizeEntryPath(url);
      const response = await fetch(`https://api.revisdom.com/api/Entry/breadcrumb/${encodeURIComponent(normalizedPath)}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          handleAuthError({ status: response.status });
        }
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }

      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading breadcrumb'
      };
    }
  };

  const getEntryByProjectId = async (projectId: number) => {
    try {
      const response = await baseEntryApi.apiEntryGet({ projectId }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading project entries'
      };
    }
  };

  const getEntryById = async (entryId: number) => {
    try {
      const response = await fetch(`https://api.revisdom.com/api/Entry/${entryId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Service: HTTP Error:', response.status, errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
     checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      console.error('Service: getEntryById hatası:', error);
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading entry details'
      };
    }
  };

  const getFileById = async (fileId: number) => {
    try {
      const response = await fetch(`https://api.revisdom.com/api/File/${fileId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading file details'
      };
    }
  };

  const createFolder = async (data: ApiFolderPostRequest) => {
    try {

      const formData = new FormData();
      if (data.name) formData.append('Name', data.name);
      if (data.parentId !== undefined) formData.append('ParentId', String(data.parentId));
      if (data.projectId !== undefined) formData.append('ProjectId', String(data.projectId));
      if (data.share && data.share.length > 0) {
        data.share.forEach(email => formData.append('Share', email));
      }
      if (data.thumbnail) formData.append('Thumbnail', data.thumbnail);
      
      // FormData içeriğini logla
      formData.forEach((value, key) => {
        
      });

      const response = await fetch('https://api.revisdom.com/api/Folder', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      if (!response.ok && response.status === 401) {
        setUser(undefined);
        const currentPath = window.location.pathname;
        const isFileDetailPage = currentPath.split('/').length >= 2;
        if (!isFileDetailPage) {
          navigate('/login');
        }
        return { success: false, message: 'Session expired' };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the folder'
      };
    }
  };

  const createFile = async (data: ApiFilePostRequest) => {
    try {
      const formData = new FormData();
      if (data.name) formData.append('Name', data.name);
      if (data.folderId !== undefined) formData.append('FolderId', String(data.folderId));
      if (data.projectId !== undefined) formData.append('ProjectId', String(data.projectId));
      if (data.status !== undefined) formData.append('Status', data.status);
      if (data.share && data.share.length > 0) {
        data.share.forEach(email => formData.append('Share', email));
      }
      if (data.thumbnail) formData.append('Thumbnail', data.thumbnail);
      if (data.file) formData.append('File', data.file);
      if (data.fileId !== undefined) formData.append('FileId', String(data.fileId));

      const response = await fetch('https://api.revisdom.com/api/File', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          setUser(undefined);
          const currentPath = window.location.pathname;
          const isFileDetailPage = currentPath.split('/').length >= 2;
          if (!isFileDetailPage) {
            navigate('/login');
          }
          return { success: false, message: 'Session expired' };
        }
        return { success: false, message: `HTTP ${response.status}: ${errorText}` };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the file'
      };
    }
  };

  const deleteEntry = async (entries: Array<{ id: number; entryType: string }>) => {
    try {
      const response = await fetch('https://api.revisdom.com/api/Entry', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({ entries }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('deleteEntry HTTP Error:', response.status, errorText);
        
        if (response.status === 401) {
          const currentPath = window.location.pathname;
          const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
          const isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login', 'results'].includes(pathSegments[0]);
          if (!isFileDetailPage) {
            window.alert('Your session has expired. You are being redirected to the login page.');
            navigate('/login');
          }
          return { success: false, message: 'Unauthorized' };
        }
        
        return { 
          success: false, 
          message: `HTTP ${response.status}: ${errorText}` 
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('deleteEntry error:', error);
      return { success: false, message: 'Failed to delete entry' };
    }
  };

  const changeStatus = async (entries: Array<{ 
    id: number; 
    entryType: string; 
    entryStatus: string; 
    changeSubentries: boolean 
  }>) => {
    try {
      const response = await fetch('https://api.revisdom.com/api/Entry/change-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({ entries }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('changeStatus HTTP Error:', response.status, errorText);
        
        if (response.status === 401) {
          return { success: false, message: 'Unauthorized' };
        }
        
        return { 
          success: false, 
          message: `HTTP ${response.status}: ${errorText}` 
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('changeStatus error:', error);
      return { success: false, message: 'Failed to change status' };
    }
  };

  const refreshUserInfo = async () => {
    try {
      const result = await baseUserApi.apiUserMeGet(postConfig());
      if (result.success) {
        setUser(result.data);
        return result.data;
      } else {
        return null;
      }
    } catch (error: any) {
      console.error('refreshUserInfo error:', error);
      
      // 401 hatası durumunda user'ı temizle
      if (error?.status === 401 || error?.response?.status === 401) {
        setUser(undefined);
        return null;
      }
      
      // Diğer hatalar için de user'ı temizle
      setUser(undefined);
      return null;
    }
  };

  const createFileComment = async (fileId: number, data: RevisdomAPIViewModelsEntryCreateCommentRequest) => {
    try {

      // API'ye doğrudan data gönder
      const response = await fetch('https://api.revisdom.com/api/File/comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Service: HTTP Error:', response.status, errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();

      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      console.error('Service: createFileComment hatası:', error);
      console.error('Service: Error details:', {
        message: error.message,
        status: error.status,
        response: error.response,
        body: error.body
      });
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the file comment'
      };
    }
  };

  const createGuestFileComment = async (fileId: number, data: RevisdomAPIViewModelsEntryCreateCommentRequest & {
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    try {
      const response = await fetch('https://api.revisdom.com/api/File/guest-comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Service: HTTP Error:', response.status, errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      return result;
    } catch (error: any) {
      console.error('Service: createGuestFileComment hatası:', error);
      return {
        success: false,
        message: 'An error occurred while creating the guest comment'
      };
    }
  };

  // Yorumun resolve bilgisini değiştirir
  const resolveFileComment = async (commentId: number, isResolved: boolean) => {
    try {
      const response = await fetch('https://api.revisdom.com/api/File/comment/resolve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId, isResolved })
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while resolving the comment'
      };
    }
  };
  // Comment düzenleme
  const editFileComment = async (commentId: number, data: { text: string; data: string }) => {
    try {
      const response = await fetch(`https://api.revisdom.com/api/File/comment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId, ...data })
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while editing the comment'
      };
    }
  };

  // Comment silme
  const deleteFileComment = async (commentId: number) => {
    try {
      const response = await fetch(`https://api.revisdom.com/api/File/comment`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId })
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while deleting the comment'
      };
    }
  };

  const getAllUserEmails = async (searchTerm?: string) => {
    try {
      const params = searchTerm ? { searchTerm } : {};
      const response = await baseUserApi.apiUserEmailsGet(params, postConfig());
      if (response.success && response.data && response.data.emails) {
        return {
          success: true,
          data: response.data.emails
        };
      }
      return { success: false, data: [] };
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading user emails',
        data: []
      };
    }
  };

  const getUserNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://api.revisdom.com/api/User/notifications', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      console.error('Fetch error:', error);
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading notifications'
      };
    }
  };

  const changeNotificationStatus = async (notificationIds: number[], isRead: boolean) => {
    try {
      const response = await fetch('https://api.revisdom.com/api/User/notifications/change-status', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          notificationIds,
          isRead
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while updating notification status'
      };
    }
  };

  // Edit functions for sharing
  const editFile = async (data: ApiFileEditPostRequest) => {
    try {
      // Multipart form data için özel config
      const formData = new FormData();
      if (data.id) formData.append('Id', data.id.toString());
      if (data.name) formData.append('Name', data.name);
      if (data.share) {
        data.share.forEach(email => formData.append('Share', email));
      }
      // Thumbnail varsa ekle
      if (data.thumbnail) {
        formData.append('Thumbnail', data.thumbnail);
      }

      const result = await baseFileApi.apiFileEditPost({
        id: data.id,
        name: data.name,
        thumbnail: data.thumbnail,
        share: data.share
      }, { 
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const editFolder = async (data: ApiFolderEditPostRequest) => {
    try {
      const result = await baseFolderApi.apiFolderEditPost({
        id: data.id,
        name: data.name,
        thumbnail: data.thumbnail,
        share: data.share
      }, { 
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const editProject = async (data: ApiProjectEditPostRequest) => {
    try {
      const result = await baseProjectApi.apiProjectEditPost({
        id: data.id,
        name: data.name,
        thumbnail: data.thumbnail,
        share: data.share
      }, { 
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const editUser = async (data: {
    firstName?: string;
    lastName?: string;
    title?: string;
    oldPassword?: string;
    newPassword?: string;
    thumbnail?: File;
    notificationToEmail?: boolean;
  }) => {
    try {
      const result = await baseUserApi.apiUserEditPost({
        firstName: data.firstName,
        lastName: data.lastName,
        title: data.title,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
        thumbnail: data.thumbnail,
        notificationToEmail: data.notificationToEmail
      } as any, { 
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Dashboard için entry count servisi
  const getEntryCount = async (status?: string, startDate?: string) => {
    try {
      const result = await baseEntryApi.apiEntryCountGet({
        startDate: startDate ? new Date(startDate) : undefined
      }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Dashboard için user dashboard servisi
  const getUserDashboard = async (startDate?: string) => {
    try {
      const result = await baseUserApi.apiUserDashboardGet({
        startDate: startDate ? new Date(startDate) : undefined
      }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Dashboard için entry summary servisi (scope: Internal | External, result için)
  const getEntrySummary = async (status?: string, startDate?: string, scope?: 'Internal' | 'External') => {
    try {
      const result = await baseEntryApi.apiEntrySummaryGet({
        startDate: startDate ? new Date(startDate) : undefined,
        status: status as any,
        scope: scope as any
      }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  return {
    doLogin,
    doLogout,
    getOrganizations,
    createUser,
    createProject,
    getProjects,
    getEnrtyDetail,
    getEntryByPath,
    getBreadcrumb,
    getEntryByProjectId,
    getEntryById,
    getFileById,
    createFolder,
    createFile,
    deleteEntry,
    changeStatus,
    refreshUserInfo,
    checkAuth,
    startSessionCheck,
    createFileComment,
    createGuestFileComment,
    getAllUserEmails,
    resolveFileComment,
    editFileComment,
    deleteFileComment,
    getUserNotifications,
    changeNotificationStatus,
    editFile,
    editFolder,
    editProject,
    editUser,
    getEntryCount,
    getUserDashboard,
    getEntrySummary
  };
};